import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-confirmation-dialog',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    template: `
        <div class="dialog-overlay" *ngIf="isOpen" (click)="onCancel()">
            <div class="dialog-container" (click)="$event.stopPropagation()">
                <div class="dialog-content">
                    <!-- Title -->
                    <h3 class="dialog-title">
                        {{ title || ('confirm_action' | translate) }}
                    </h3>

                    <!-- Message -->
                    <p class="dialog-message">
                        {{ message }}
                    </p>

                    <!-- Actions -->
                    <div class="dialog-actions">
                        <button
                            type="button"
                            class="dialog-btn dialog-btn-cancel"
                            (click)="onCancel()"
                        >
                            {{
                                cancelText || ('cancel' | translate) || 'Cancel'
                            }}
                        </button>
                        <button
                            type="button"
                            class="dialog-btn dialog-btn-confirm"
                            (click)="onConfirm()"
                        >
                            {{
                                confirmText ||
                                    ('confirm' | translate) ||
                                    'Confirm'
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .dialog-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.4);
                backdrop-filter: blur(2px);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10000;
                padding: 1rem;
                animation: fadeIn 0.25s ease-out;
            }

            @keyframes fadeIn {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }

            .dialog-container {
                background: #ffffff;
                border-radius: 0.7rem;
                max-width: 26rem;
                width: 100%;
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
                animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                overflow: hidden;
            }

            @keyframes slideUp {
                from {
                    transform: translateY(30px) scale(0.96);
                    opacity: 0;
                }
                to {
                    transform: translateY(0) scale(1);
                    opacity: 1;
                }
            }

            .dialog-content {
                padding: 2rem 2rem 1.5rem;
                text-align: center;
            }

            .dialog-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 3.5rem;
                height: 3.5rem;
                margin: 0 auto 1.25rem;
                background: linear-gradient(135deg, #fff5e6 0%, #ffe4b3 100%);
                border-radius: 50%;
                position: relative;

                svg {
                    filter: drop-shadow(0 1px 2px rgba(245, 158, 11, 0.2));
                }
            }

            .dialog-title {
                font-size: 1.375rem;
                font-weight: 700;
                color: #221f20;
                margin: 0 0 0.75rem 0;
                letter-spacing: -0.025em;
            }

            .dialog-message {
                font-size: 0.9375rem;
                color: #737678;
                margin: 0 0 2rem 0;
                line-height: 1.65;
                font-weight: 400;
            }

            .dialog-actions {
                display: flex;
                gap: 0.75rem;
                justify-content: center;
                padding: 1.5rem 2rem;
                background: #f8f9fa;
                border-top: 1px solid #e9ecef;
                margin: 0 -2rem -1.5rem;
            }

            .dialog-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0.625rem 2rem;
                border-radius: 3rem;
                font-size: 0.875rem;
                font-weight: 700;
                border: none;
                cursor: pointer;
                transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                min-width: 110px;
                height: 42px;
                letter-spacing: 0.025em;
                position: relative;
                overflow: hidden;
            }

            .dialog-btn::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                width: 0;
                height: 0;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.3);
                transform: translate(-50%, -50%);
                transition: width 0.4s, height 0.4s;
            }

            .dialog-btn:active::before {
                width: 300px;
                height: 300px;
            }

            .dialog-btn-cancel {
                background: #f3f4f6;
                color: #4b5563;
                border: 1px solid #e5e7eb;
            }

            .dialog-btn-cancel:hover {
                background: #e5e7eb;
                border-color: #d1d5db;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            .dialog-btn-cancel:active {
                transform: translateY(0);
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .dialog-btn-confirm {
                background: #221f20;
                color: #ffffff;
                box-shadow: 0 2px 8px rgba(34, 31, 32, 0.25);
            }

            .dialog-btn-confirm:hover {
                background: #3a3738;
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(34, 31, 32, 0.3);
            }

            .dialog-btn-confirm:active {
                transform: translateY(0);
                box-shadow: 0 2px 6px rgba(34, 31, 32, 0.25);
            }

            /* Mobile responsiveness */
            @media (max-width: 640px) {
                .dialog-overlay {
                    padding: 1rem;
                    align-items: flex-end;
                }

                .dialog-container {
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                    max-width: 100%;
                    animation: slideUpMobile 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                @keyframes slideUpMobile {
                    from {
                        transform: translateY(100%);
                    }
                    to {
                        transform: translateY(0);
                    }
                }

                .dialog-content {
                    padding: 1.75rem 1.5rem 1.25rem;
                }

                .dialog-icon {
                    width: 3rem;
                    height: 3rem;
                    margin-bottom: 1rem;

                    svg {
                        width: 40px;
                        height: 40px;
                    }
                }

                .dialog-title {
                    font-size: 1.25rem;
                }

                .dialog-message {
                    font-size: 0.875rem;
                    margin-bottom: 1.5rem;
                }

                .dialog-actions {
                    flex-direction: column-reverse;
                    gap: 0.625rem;
                    padding: 1.25rem 1.5rem;
                    margin: 0 -1.5rem -1.25rem;
                }

                .dialog-btn {
                    width: 100%;
                    min-width: auto;
                    height: 48px;
                    font-size: 0.9375rem;
                }
            }

            /* RTL Support */
            [dir='rtl'] .dialog-content {
                text-align: center;
            }

            /* Accessibility */
            .dialog-btn:focus {
                outline: 2px solid #221f20;
                outline-offset: 3px;
            }

            .dialog-btn-cancel:focus {
                outline: 2px solid #9ca3af;
            }

            .dialog-btn:focus:not(:focus-visible) {
                outline: none;
            }

            /* Reduced motion */
            @media (prefers-reduced-motion: reduce) {
                .dialog-overlay,
                .dialog-container,
                .dialog-btn {
                    animation: none;
                    transition: none;
                }
            }

            /* Dark mode support (optional) */
            @media (prefers-color-scheme: dark) {
                .dialog-container {
                    background: #ffffff;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
                }

                .dialog-actions {
                    background: #f8f9fa;
                    border-top-color: #e9ecef;
                }
            }
        `
    ]
})
export class ConfirmationDialogComponent {
    @Input() isOpen = false;
    @Input() title?: string;
    @Input() message: string = '';
    @Input() confirmText?: string;
    @Input() cancelText?: string;

    @Output() confirm = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    onConfirm(): void {
        this.confirm.emit();
    }

    onCancel(): void {
        this.cancel.emit();
    }
}
